import React from 'react'
import { GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from '@mui/x-data-grid-pro'
import { type GridCsvExportOptions } from '@mui/x-data-grid/models/gridExport'
import { type GridExportDisplayOptions } from '@mui/x-data-grid/components/toolbar/GridToolbarExport'
import { Chip, Divider, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneIcon from '@mui/icons-material/Tune'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { type KeyedMutator } from 'swr'
import {
  MODE_OPTIONS, type WrappedDataGridPropsGridMode,
  type WrappedDataGridPropsStableCoinVisibility, type WrappedDataGridPropsToolbarData,
} from './wrapped-data-grid-types'
import { isEmptyString, toTitleCase } from '../../../utils/string-utils'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../../utils/time-utils'
import { formatRoundedUsdAmount } from '../../../utils/rounding-formatting-utils'

export interface WrappedGridToolbarProps {
  gridMode: WrappedDataGridPropsGridMode
  stableCoinVisibility?: WrappedDataGridPropsStableCoinVisibility
  mutate: KeyedMutator<any>
  portfolio: string
  toolbarData?: WrappedDataGridPropsToolbarData
  csvOptions?: GridCsvExportOptions & GridExportDisplayOptions
}

const WrappedGridToolbar: React.FC<WrappedGridToolbarProps> = (
  {
    gridMode, stableCoinVisibility, mutate,
    portfolio, toolbarData, csvOptions
  }
) => {
  const { mode, modeOptions, setMode } = gridMode
  const {
    stableCoinVisibility: stableCoinVisibilityState,
    toggleStableCoinVisibility: toggleStableCoinVisibilityState
  } = stableCoinVisibility ?? { stableCoinVisibility: null, toggleStableCoinVisibility: () => {} }

  const timestamp = toolbarData?.timestamp
  const totalCurrAsset = toolbarData?.totalCurrAsset
  const pnlAsset = toolbarData?.pnlAsset
  const nextFund = toolbarData?.nextFund
  const initialBalanceByCoin = toolbarData?.initialBalanceByCoin
  const initialExposureByCoin = toolbarData?.initialExposureByCoin

  return (
    <GridToolbarContainer sx={{ paddingX: 1 }}>
      <Grid container alignItems='center' rowGap={1}>
        <Grid item display='flex' gap={1}>
          <Typography color='secondary'>Portfolio:</Typography>
          <Typography>{portfolio}</Typography>
        </Grid>

        {
          timestamp != null && timestamp !== 0 &&
          <Divider orientation='vertical' variant='middle' flexItem sx={{ marginX: 1 }}/>
        }
        {
          timestamp != null && timestamp !== 0 &&
          <Grid item display='flex' flexShrink={0} gap={1} sx={{
            flexGrow: {
              xs: '1',
              md: '0',
            },
            justifyContent: {
              xs: 'center',
              md: 'flex-start',
            }
          }}>
            <Chip
              label={formatToLocalMilliTimestampFromUTCNumber(timestamp)}
              size='small'
            />
          </Grid>
        }

        {
          mutate != null &&
          <Divider orientation='vertical' variant='middle' flexItem sx={{ marginX: 1 }}/>
        }
        {
          mutate != null &&
          <Grid item>
            <Tooltip title='Refresh'>
              <IconButton
                size='small'
                onClick={() => {
                  void (async () => {
                    await mutate()
                  })()
                }}
              >
                <RefreshIcon/>
              </IconButton>
            </Tooltip>
          </Grid>
        }

        {
          modeOptions.filter(modeOption => modeOption !== MODE_OPTIONS.NONE).length > 0 &&
          <Divider orientation='vertical' variant='middle' flexItem sx={{ marginX: 1 }}/>
        }
        {
          modeOptions.filter(modeOption => modeOption !== MODE_OPTIONS.NONE).length > 0 &&
          <Grid item sx={{
            flexGrow: {
              xs: '1',
              md: '0',
            }
          }}>
            <Stack
              direction='row'
              columnGap={2}
              sx={{
                justifyContent: {
                  xs: 'center',
                  md: 'flex-start',
                }
              }}
            >
              <Tooltip title='Group Rows'>
                <TuneIcon color='secondary'/>
              </Tooltip>
              {
                modeOptions.map(modeOption => {
                  if (modeOption === MODE_OPTIONS.NONE) {
                    return null
                  } else {
                    return (
                      <Chip
                        key={modeOption}
                        label={`By ${toTitleCase(modeOption)}`}
                        onClick={() => {
                          setMode(modeOption)
                        }}
                        variant='outlined'
                        size='small'
                        color={mode === modeOption ? 'primary' : undefined}
                      />
                    )
                  }
                })
              }
            </Stack>
          </Grid>
        }

        {
          stableCoinVisibilityState != null && mode === MODE_OPTIONS.COIN &&
          <Divider orientation='vertical' variant='middle' flexItem sx={{ marginX: 1 }}/>
        }
        {
          stableCoinVisibilityState != null && mode === MODE_OPTIONS.COIN &&
          <Grid item>
            <Tooltip title='Toggle Visibility of USD(C/T)'>
              <IconButton
                size='small'
                onClick={() => {
                  toggleStableCoinVisibilityState()
                }}
              >
                {
                  stableCoinVisibilityState ? <VisibilityIcon/> : <VisibilityOffIcon/>
                }
              </IconButton>
            </Tooltip>
          </Grid>
        }

        <Divider orientation='vertical' variant='middle' flexItem sx={{ marginX: 1 }}/>
        <Grid item display='flex' flexDirection='row' gap={2} sx={{
          flexGrow: {
            xs: '1',
            md: '0',
          },
          justifyContent: {
            xs: 'center',
            md: 'flex-start',
          }
        }}>
          <GridToolbarFilterButton/>
          <GridToolbarExport csvOptions={csvOptions}/>
        </Grid>

        <Grid item display='flex' flexDirection='row' gap={2} flexGrow={1} sx={{
          justifyContent: {
            xs: 'flex-start',
            xl: 'flex-end',
          },
          overflowX: 'auto'
        }}>
          {
            !isEmptyString(totalCurrAsset) &&
            <Grid item display='flex' flexShrink={0} gap={1}>
              <Typography color='secondary'>Balance:</Typography>
              <Typography>{formatRoundedUsdAmount(totalCurrAsset as string)}</Typography>
            </Grid>
          }
          {
            !isEmptyString(pnlAsset) &&
            <Grid item display='flex' flexShrink={0} gap={1}>
              <Typography color='secondary'>PnL:</Typography>
              <Typography>{formatRoundedUsdAmount(pnlAsset as string)}</Typography>
            </Grid>
          }
          {
            !isEmptyString(nextFund) &&
            <Grid item display='flex' flexShrink={0} gap={1}>
              <Typography color='secondary'>Next Fund:</Typography>
              <Typography>{formatRoundedUsdAmount(nextFund as string)}</Typography>
            </Grid>
          }
        </Grid>
      </Grid>

      {
        initialBalanceByCoin != null && Object.keys(initialBalanceByCoin).length > 0 &&
        <Grid item display='flex' gap={1} sx={{ overflowX: 'auto' }}>
          <Typography color='secondary'>Initial Balance:</Typography>
          <Stack direction='row' alignItems='center' gap={1} divider={<Divider orientation='vertical' flexItem/>}>
            {
              Object.entries(initialBalanceByCoin)
                .sort(([key1], [key2]) => key1.localeCompare(key2, undefined, { numeric: true }))
                .map(([key, value]) => {
                  return (
                    <Typography key={key}>{key}: {formatRoundedUsdAmount(value)}</Typography>
                  )
                })
            }
          </Stack>
        </Grid>
      }

      {
        initialExposureByCoin != null && Object.keys(initialExposureByCoin).length > 0 &&
        <Grid item display='flex' gap={1} sx={{ overflowX: 'auto' }}>
          <Typography color='secondary'>Initial Exposure:</Typography>
          <Stack direction='row' alignItems='center' gap={1} divider={<Divider orientation='vertical' flexItem/>}>
            {
              Object.entries(initialExposureByCoin)
                .sort(([key1], [key2]) => key1.localeCompare(key2, undefined, { numeric: true }))
                .map(([key, value]) => {
                  return (
                    <Typography key={key}>{key}: {formatRoundedUsdAmount(value)}</Typography>
                  )
                })
            }
          </Stack>
        </Grid>
      }
    </GridToolbarContainer>
  )
}

export default WrappedGridToolbar
